/**
 * GridEngine — Core CSS Grid renderer for the flexible layout engine.
 *
 * Takes a GridLayoutConfig (rows/cells) and a sectionRegistry,
 * renders an explicit CSS Grid with sections placed into cells.
 *
 * Mobile: collapses to 1-column stacked.
 * Tablet: collapses to 2 columns max (cells wider than 2 get clamped).
 * Desktop: full column layout as configured.
 */

import React, { useMemo } from 'react'
import { getGridTemplateColumns } from '../config/gridSchema'
import { widthToColSpan, heightToRowSpan } from '../config/gridTemplates'

// Responsive breakpoints matching Tailwind defaults
const BREAKPOINTS = {
  mobile: 0,
  tablet: 768,   // md
  desktop: 1024,  // lg
}

// Explicit Tailwind column class map (avoids dynamic class generation)
const GRID_COL_CLASSES = {
  md: { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' },
  lg: { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' },
}

/**
 * Resolve a cell's effective properties at the current viewport.
 * If sectionConfig has width/height overrides, those take priority
 * over the cell's static colSpan/rowSpan (admin control bridge).
 */
function resolveCell(cell, columns, sectionConfig) {
  let colSpan = Math.min(cell.colSpan || 1, columns)
  let rowSpan = cell.rowSpan || 1

  // Bridge: admin width/height controls → grid colSpan/rowSpan
  if (sectionConfig) {
    if (sectionConfig.width) {
      colSpan = Math.min(widthToColSpan(sectionConfig.width, columns), columns)
    }
    if (sectionConfig.height) {
      rowSpan = heightToRowSpan(sectionConfig.height)
    }
    // stretch is vertical-only (flex equal-height) — handled by SectionItem,
    // NOT a horizontal full-width override
  }

  return {
    colSpan,
    colStart: cell.colStart || undefined,
    rowSpan,
    hidden: cell.hidden || false,
    variant: cell.variant || 'default',
    sectionType: cell.sectionType,
    config: cell.config || {},
  }
}

function GridCell({ cell, columns, renderSection, editMode, sectionConfigs }) {
  const sectionConfig = sectionConfigs?.[cell.sectionType]?.config
  const resolved = useMemo(() => resolveCell(cell, columns, sectionConfig), [cell, columns, sectionConfig])

  if (resolved.hidden && !editMode) return null

  const style = {
    gridColumn: resolved.colStart
      ? `${resolved.colStart} / span ${resolved.colSpan}`
      : `span ${resolved.colSpan}`,
  }

  if (resolved.rowSpan > 1) {
    style.gridRow = `span ${resolved.rowSpan}`
  }

  // Render the section via the registry
  const content = renderSection(resolved.sectionType, {
    variant: resolved.variant,
    cellConfig: resolved.config,
    editMode,
  })

  if (!content) {
    // Unknown section type — show placeholder in edit mode only
    if (editMode) {
      return (
        <div
          style={style}
          className="border-2 border-dashed border-yellow-400/50 rounded-lg p-4 flex items-center justify-center text-yellow-600 text-sm"
        >
          Unknown: {resolved.sectionType}
        </div>
      )
    }
    return null
  }

  return (
    <div style={style} className={resolved.hidden ? 'opacity-40' : ''}>
      {content}
    </div>
  )
}

/**
 * GridEngine — main component.
 *
 * @param {Object} layout - GridLayoutConfig object
 * @param {Function} renderSection - (sectionType, context) => ReactNode
 * @param {boolean} editMode - whether admin edit mode is active
 * @param {string} className - additional CSS classes
 * @param {Object} sectionConfigs - optional map of sectionType → section data (with .config for width/height/stretch overrides)
 */
export default function GridEngine({ layout, renderSection, editMode = false, className = '', sectionConfigs }) {
  const { columns, rows, theme } = layout

  const gridClasses = useMemo(() => {
    const base = [
      'grid',
      'gap-x-6 gap-y-8',
      'items-stretch',
      'grid-cols-1',
    ]

    // Tablet: min(columns, 2)
    const tabletCols = Math.min(columns, 2)
    base.push(GRID_COL_CLASSES.md[tabletCols] || 'md:grid-cols-1')

    // Desktop: full columns
    base.push(GRID_COL_CLASSES.lg[columns] || 'lg:grid-cols-1')

    return base.join(' ')
  }, [columns])

  // Theme classes
  const themeClasses = useMemo(() => {
    switch (theme) {
      case 'dark':
        return 'bg-gray-900 text-white'
      case 'gradient':
        return 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white'
      default:
        return ''
    }
  }, [theme])

  // Flatten all cells and sort by section_order so admin reordering is reflected.
  // Rows render as fragments so all cells share the same CSS grid parent.
  const sortedCells = useMemo(() => {
    if (!rows) return []
    const flat = rows.flatMap(row => row.cells)

    // Synthesize overflow cells for sections in sectionConfigs but not in template
    if (sectionConfigs) {
      const templateTypes = new Set(flat.map(c => c.sectionType))
      Object.keys(sectionConfigs).forEach(sType => {
        if (!templateTypes.has(sType)) {
          flat.push({
            id: `overflow-${sType}`,
            sectionType: sType,
            colSpan: columns,
            rowSpan: 1,
          })
        }
      })
    }

    if (!sectionConfigs) return flat
    return [...flat].sort((a, b) => {
      const orderA = sectionConfigs[a.sectionType]?.section_order ?? Infinity
      const orderB = sectionConfigs[b.sectionType]?.section_order ?? Infinity
      return orderA - orderB
    })
  }, [rows, sectionConfigs, columns])

  if (!rows || rows.length === 0) {
    if (editMode) {
      return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-400">
          <p className="text-lg font-medium">Empty Grid Layout</p>
          <p className="text-sm mt-1">Add rows and sections to build your page</p>
        </div>
      )
    }
    return null
  }

  return (
    <div className={`grid-engine ${themeClasses} ${className}`.trim()}>
      <div className={gridClasses} style={{ gridAutoRows: 'minmax(5rem, auto)' }}>
        {sortedCells.map(cell => (
          <GridCell
            key={cell.id}
            cell={cell}
            columns={columns}
            renderSection={renderSection}
            editMode={editMode}
            sectionConfigs={sectionConfigs}
          />
        ))}
      </div>
    </div>
  )
}

// Re-export for external use
GridEngine.getGridTemplateColumns = getGridTemplateColumns
